import { useMemo, useState } from 'react';
import { SearchCodeModalForm } from 'src/components/common/form/search/SearchCodeModalForm';
import { SearchDropDownForm } from 'src/components/common/form/search/SearchDropDownForm';
import { SearchSectionForm } from 'src/components/common/form/search/SearchSectionForm';
import { SearchYearSingleForm } from 'src/components/common/form/search/SearchYearSingleForm';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { useHRM1060_R01Store } from './Store_HRM1060_R01';

/** 상위 화면의 조회 버튼을 검색 입력의 Enter와 연결한다. */
interface Props {
    fetchData: () => void; // 조회 함수
}

/** 신청년도·증명서·상태·사원 조건을 표시한다. @param fetchData 조회 함수 @returns 검색 영역 */
export const SearchSection_HRM1060_R01 = ({ fetchData }: Props) => {
    const { HRM1060_R01Filter, setHRM1060_R01Filter } = useHRM1060_R01Store(); // 현재 조건과 변경 함수
    const { certTypeList, certStsList } = useCommonCodeStore(); // 앱 초기화 때 받은 증명서 공통코드
    const { setCodeSearchModalProps } = useUiuxStore(); // 사원 검색 팝업 열기
    const [isYearPickerVisible, setIsYearPickerVisible] = useState(false); // true이면 연도 달력을 표시
    const [yearPickerPosition, setYearPickerPosition] = useState({ top: 0, left: 0 }); // 연도 달력 위치
    const issueYearValue = useMemo(() => {
        const year = Number(HRM1060_R01Filter.issueYear); // 선택한 연도 숫자
        return year >= 1000 && year <= 9999 ? new Date(year, 0, 1) : undefined;
    }, [HRM1060_R01Filter.issueYear]); // 연도 문자열이 바뀔 때만 Date를 다시 만든다

    /** 사원 선택 결과의 사번과 표시명을 검색 조건에 기록한다. @param search 팝업 초기 검색어 */
    const openEmployeeSearch = (search: string) => {
        setCodeSearchModalProps({
            category: '사원', search, formName: 'HRM1060_R01', isInfo: false,
            handleFoucsId: 'HRM1060_R01-employee-search',
            onConfirm: employee => setHRM1060_R01Filter({
                keyword: employee?.empNo || '', empNm: employee?.empNm || '',
            }),
            processType_focus: 'normal',
        });
    };

    return (
        <SearchSectionForm>
            {/* 신청년도는 발급일의 연도 범위를 고른다. */}
            <SearchYearSingleForm label="신청년도" visible={isYearPickerVisible}
                setVisible={setIsYearPickerVisible} value={issueYearValue}
                onChange={date => {
                    if (date) setHRM1060_R01Filter({ issueYear: String(date.getFullYear()) });
                }}
                pickerPosition={yearPickerPosition} setPickerPosition={setYearPickerPosition}
                onEnterKeyDown={fetchData} />
            {/* 증명서 종류는 CERT_TYPE 코드값으로 검색한다. */}
            <SearchDropDownForm label="증명서" value={HRM1060_R01Filter.certType}
                optionList={certTypeList}
                onChangeItem={item => setHRM1060_R01Filter({ certType: item?.minorCd || '' })} />
            {/* 발급 상태는 CERT_STS 코드값으로 검색한다. */}
            <SearchDropDownForm label="상태" value={HRM1060_R01Filter.issueSts}
                optionList={certStsList}
                onChangeItem={item => setHRM1060_R01Filter({ issueSts: item?.minorCd || '' })} />
            {/* 사원은 더블클릭으로 공통 검색 팝업을 열고 Enter로 조회한다. */}
            <SearchCodeModalForm label="사원" value={HRM1060_R01Filter.keyword}
                valueLabel={HRM1060_R01Filter.empNm}
                onChange={event => setHRM1060_R01Filter({ keyword: event.target.value, empNm: '' })}
                onDoubleClick={event => {
                    event.currentTarget.blur();
                    openEmployeeSearch(HRM1060_R01Filter.keyword);
                }}
                onKeyDown={event => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    fetchData();
                }} />
        </SearchSectionForm>
    );
};
